/**
 * Lịch phát bảng tin (bảng điện tử công cộng) — API `/api/v1/digital-signage/schedules*`.
 *
 *   - `LichPhatBangTin` `/bang-tin-dien-tu/lich-phat`: lọc địa bàn / tên / trạng thái / khoảng ngày
 *     ở máy chủ, phân trang 20 dòng; thao tác Sửa, Phát lịch (gửi lại DV010), Hủy lịch (DV012).
 *     Hợp đồng A0 mục 4 KHÔNG có `schedules/export` nên trang không có nút Xuất Excel.
 *   - `SoanLichBangTin` `/bang-tin-dien-tu/lich-phat/moi` và `/:id/sua`: bố cục như trang "Lịch phát
 *     thanh" (tên lịch, địa bàn, thiết bị, ngày phát → khung giờ → bản tin) thêm ô "Phân vùng màn
 *     hình" (Toàn màn hình / Phân vùng màn hình). Khung giờ cùng danh sách bản tin gom thành một
 *     lịch theo ngày (epoch tuyệt đối, giờ Việt Nam).
 *
 * Gộp kho (29/10/2026): bản tin chọn vào khung giờ lấy từ Kho nội dung `GET /api/v1/bulletins`
 * (`trangThai=da_duyet,dang_phat,da_phat`, `loaiBanTin=1,2,3,4`), gửi `id` uuid `ban_tin.id`.
 */
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { dienVao } from '../ngon-ngu/vi';
import { viBangTin } from '../ngon-ngu/vi-bang-tin';
import { viLichNhatKy } from '../ngon-ngu/vi-lich-nhat-ky';
import { viLichPhatThanh } from '../ngon-ngu/vi-lich-phat-thanh';
import {
  bangTinApi,
  banTinChonTuKho,
  idBanTinCuaLich,
  ketQuaGuiCuaLich,
  type ChiTietLichBT,
  type BangTinApi,
  type BanTinChonBT,
  type BanTinTrongLichBT,
  type KhungGioBT,
  type LichPhatBT,
  type LichPhatBTVao,
  type PhanVungManHinhBT,
  type TrangDuLieu,
  type TrangThaiThietBiBT,
} from '../api/bang-tin-api';
import { thietBiApi, type NutDiaBan } from '../api/thiet-bi-api';
import {
  banTinApi,
  LOAI_BAN_TIN_BANG_TIN,
  TRANG_THAI_CHON_PHAT,
  type BoLocBanTin,
  type DongBanTin,
} from '../api/ban-tin-api';
import { dungHoan, dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag, TruongLoc } from '../thanh-phan/MauGiaoDien';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { ChonDiaBanCay, boDau } from '../thanh-phan/ChonDiaBanCay';
import { HopChonNgay } from '../thanh-phan/HopChonNgay';
import { hienThongBaoNhanh } from '../thanh-phan/ThongBaoNhanh';
import { ngayVnSangEpoch, OKhoangNgay } from './DanhSachLichPhat';
import { gioNgayVn } from './NhatKyLichPhatThietBi';
import { DaiThongBao, loiThanhChuoi, ngayVn, thongBaoKetQuaGui } from './BangTinChung';
import { OGio } from './LichPhatThanh';
import {
  MOT_NGAY_GIAY,
  epochVn,
  hienThiNgay,
  hmsTuGiay,
  homNayVn,
  ketThucKhung,
  kiemLoiLich,
  tenKhung,
  tongThoiLuong,
  type KhungGioSoan,
  type NgayPhatSoan,
} from './lich-phat-thanh-tien-ich';
import './danh-sach-lich-phat.css';
import './bien-tap.css';
import './bang-tin.css';
import './lich-phat-thanh.css';

const t = viBangTin.lich;
const tc = viBangTin.chung;
const DUONG_DS = '/bang-tin-dien-tu/lich-phat';
const DS_TRANG_THAI = ['hieu_luc', 'het_hieu_luc', 'da_huy'] as const;

export type ApiLichBT = Pick<
  BangTinApi,
  'lichDanhSach' | 'lichChiTiet' | 'lichTao' | 'lichSua' | 'lichHuy' | 'lichGuiLai' | 'trangThaiThietBi'
> & {
  cayDiaBan: (signal?: AbortSignal) => Promise<NutDiaBan[]>;
  /** Kho nội dung `GET /api/v1/bulletins` (gộp kho bản tin). */
  banTinDanhSach: (tham: BoLocBanTin, signal?: AbortSignal) => Promise<TrangDuLieu<DongBanTin>>;
};

export const API_LICH_BT_MAC_DINH: ApiLichBT = {
  ...bangTinApi,
  cayDiaBan: (signal) => thietBiApi.cayDiaBan({}, signal),
  banTinDanhSach: banTinApi.danhSach,
};

/** Mã địa bàn `ma` có nằm trong (bằng hoặc dưới) địa bàn `goc` không. */
export function thuocDiaBan(ma: string | null | undefined, goc: string): boolean {
  return !!ma && (ma === goc || ma.startsWith(`${goc}.`));
}

/** Ô "Thời gian phát" của lịch. */
function thoiGianLich(l: LichPhatBT): string {
  if (l.loaiLich === 'hang_ngay') return `${ngayVn(l.ngayBatDauPhat)} – ${ngayVn(l.ngayKetThucPhat)}`;
  return typeof l.thoiDiemBatDau === 'number' ? gioNgayVn(l.thoiDiemBatDau) : tc.khongCo;
}

/* --------------------------------- Danh sách --------------------------------- */

export function LichPhatBangTin({ api = API_LICH_BT_MAC_DINH }: { api?: ApiLichBT } = {}): ReactNode {
  const phien = dungPhien();
  const viTri = useLocation();
  const trangThaiDen = viTri.state as { thongBao?: unknown; loai?: unknown } | null;
  const thongBaoDen = trangThaiDen?.thongBao;
  const [nguonId, datNguonId] = useState('');
  const [oTim, datOTim] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [trangThai, datTrangThai] = useState('');
  const [tuNgay, datTuNgay] = useState('');
  const [denNgay, datDenNgay] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState<number>(SO_DONG_MAC_DINH);
  const [dangHuy, datDangHuy] = useState<LichPhatBT | null>(null);
  const [dangPhat, datDangPhat] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<{ loai: 'ok' | 'loi'; noiDung: string } | null>(
    typeof thongBaoDen === 'string'
      ? { loai: trangThaiDen?.loai === 'loi' ? 'loi' : 'ok', noiDung: thongBaoDen }
      : null,
  );

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const dangLoc = nguonId !== '' || tuKhoa !== '' || trangThai !== '' || tuNgay !== '' || denNgay !== '';
  const kq = dungTaiDuLieu<TrangDuLieu<LichPhatBT>>({
    tai: (signal) =>
      api.lichDanhSach(
        {
          page: trang,
          pageSize: soDong,
          nguonId: nguonId || undefined,
          keyword: tuKhoa || undefined,
          trangThai: trangThai || undefined,
          tuThoiDiem: ngayVnSangEpoch(tuNgay),
          denThoiDiem: ngayVnSangEpoch(denNgay, true),
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonId, tuKhoa, trangThai, tuNgay, denNgay],
  });

  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const xoaLoc = (): void => {
    datNguonId('');
    datOTim('');
    datTuKhoa('');
    datTrangThai('');
    datTuNgay('');
    datDenNgay('');
    datTrang(1);
  };

  const phatLich = async (l: LichPhatBT): Promise<void> => {
    datDangPhat(l.id);
    try {
      const r = await api.lichGuiLai(l.id);
      const tb = thongBaoKetQuaGui(ketQuaGuiCuaLich(r), dienVao(t.daPhat, { ten: l.tenLichPhat }));
      datThongBao(tb);
      if (tb.loai === 'ok') hienThongBaoNhanh(tb.noiDung);
    } catch (e) {
      datThongBao({ loai: 'loi', noiDung: dienVao(tc.loiThaoTac, { loi: loiThanhChuoi(e) }) });
    } finally {
      datDangPhat(null);
    }
  };

  const huyLich = async (l: LichPhatBT): Promise<void> => {
    try {
      await api.lichHuy(l.id);
      const noiDung = dienVao(t.daHuy, { ten: l.tenLichPhat });
      datThongBao({ loai: 'ok', noiDung });
      hienThongBaoNhanh(noiDung);
      kq.taiLai();
    } catch (e) {
      datThongBao({ loai: 'loi', noiDung: dienVao(tc.loiThaoTac, { loi: loiThanhChuoi(e) }) });
    }
  };

  const duocQuanLy = phien.coQuyen('signage.manage');
  const dsDong = kq.duLieu?.items ?? [];
  const batDauStt = (trang - 1) * soDong;

  const cot: CotBang<LichPhatBT>[] = [
    {
      khoa: 'cham',
      nhan: '',
      canGiua: true,
      ve: (l) => (
        <span
          className={`dslp-cham dslp-cham--${l.trangThai === 'hieu_luc' ? 'con_hieu_luc' : l.trangThai}`}
          data-hieu-luc={l.trangThai}
          title={viBangTin.trangThaiLich[l.trangThai]}
        />
      ),
    },
    { khoa: 'stt', nhan: tc.stt, canGiua: true, ve: (l) => batDauStt + dsDong.indexOf(l) + 1 },
    {
      khoa: 'ten',
      nhan: t.cot.ten,
      ve: (l) => (
        <>
          <strong className="nklt-ten">{l.tenLichPhat}</strong>
          <span className="nklt-phu">{dienVao(t.ma, { ma: l.lichPhatId })}</span>
        </>
      ),
    },
    { khoa: 'loai', nhan: t.cot.loai, ve: (l) => t.loaiLich[l.loaiLich] ?? l.loaiLich },
    {
      khoa: 'phanVung',
      nhan: t.cot.phanVung,
      ve: (l) => viBangTin.phanVungManHinh[l.phanVungManHinh] ?? l.phanVungManHinh,
    },
    {
      khoa: 'thoiGian',
      nhan: t.cot.thoiGian,
      ve: (l) => (
        <>
          <span className="dslp-khong-xuong">{thoiGianLich(l)}</span>
          <span className="nklt-phu">{dienVao(t.soKhungGio, { so: l.soKhungGio ?? 0 })}</span>
        </>
      ),
    },
    { khoa: 'soBanTin', nhan: t.cot.soBanTin, canGiua: true, ve: (l) => l.soBanTin ?? 0 },
    {
      khoa: 'diaBan',
      nhan: t.cot.diaBan,
      ve: (l) => (l.diaBan ?? []).map((p) => p.tenDich ?? p.dichId).join(', ') || tc.khongCo,
    },
    {
      khoa: 'trangThai',
      nhan: t.cot.trangThai,
      ve: (l) => (
        <TheTag kieu={l.trangThai === 'hieu_luc' ? 'ok' : l.trangThai === 'da_huy' ? 'do' : 'trung-tinh'}>
          {viBangTin.trangThaiLich[l.trangThai] ?? l.trangThai}
        </TheTag>
      ),
    },
    {
      khoa: 'thaoTac',
      nhan: tc.thaoTac,
      ve: (l) =>
        duocQuanLy && l.trangThai === 'hieu_luc' ? (
          <span className="dslp-thao-tac">
            <Link
              to={`${DUONG_DS}/${encodeURIComponent(l.id)}/sua`}
              className="nut nut--nho dslp-nut dslp-nut--sua"
              data-hanh-dong="sua-lich"
              aria-label={`${t.sua} ${l.tenLichPhat}`}
            >
              {t.sua}
            </Link>
            <>
              <button
                type="button"
                className="nut nut--nho dslp-nut dslp-nut--phat"
                data-hanh-dong="phat-lich"
                aria-label={`${t.phat} ${l.tenLichPhat}`}
                disabled={dangPhat === l.id}
                onClick={() => void phatLich(l)}
              >
                {dangPhat === l.id ? t.dangPhat : t.phat}
              </button>
              <button
                type="button"
                className="nut nut--nho dslp-nut dslp-nut--huy"
                data-hanh-dong="huy-lich"
                aria-label={`${t.huy} ${l.tenLichPhat}`}
                onClick={() => datDangHuy(l)}
              >
                {t.huy}
              </button>
            </>
          </span>
        ) : null,
    },
  ];

  return (
    <section className="man-hinh dslp" data-man-hinh="lich-phat-bang-tin">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
      </header>

      <div className="dslp-loc" role="search">
        <TruongLoc nhan={tc.diaBan} htmlFor="lbt-dia-ban">
          <ChonDiaBanCay
            id="lbt-dia-ban"
            className="dslp-cay"
            nhan={tc.diaBan}
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            nhanTatCa={tc.toanTinh}
            goiYTim={tc.timDiaBan}
            onDoi={doiLoc(datNguonId)}
          />
        </TruongLoc>
        <TruongLoc nhan={t.tenLich} htmlFor="lbt-ten">
          <form
            className="dslp-tim"
            onSubmit={(e) => {
              e.preventDefault();
              datTuKhoa(oTim.trim());
              datTrang(1);
            }}
          >
            <input
              id="lbt-ten"
              type="search"
              placeholder={t.nhapTenLich}
              value={oTim}
              onChange={(e) => datOTim(e.target.value)}
            />
            <button type="submit" className="nut dslp-tim__nut" aria-label={tc.timKiem} title={tc.timKiem}>
              {tc.tim}
            </button>
          </form>
        </TruongLoc>
        <TruongLoc nhan={t.trangThai} htmlFor="lbt-trang-thai">
          <select
            id="lbt-trang-thai"
            value={trangThai}
            onChange={(e) => doiLoc(datTrangThai)(e.target.value)}
          >
            <option value="">{t.chonTrangThai}</option>
            {DS_TRANG_THAI.map((s) => (
              <option key={s} value={s}>
                {viBangTin.trangThaiLich[s]}
              </option>
            ))}
          </select>
        </TruongLoc>
        <OKhoangNgay
          tienTo="lbt"
          tuNgay={tuNgay}
          denNgay={denNgay}
          onDoiTu={doiLoc(datTuNgay)}
          onDoiDen={doiLoc(datDenNgay)}
        />
      </div>

      <div className="dslp-hanh-dong">
        <Link to={`${DUONG_DS}/moi`} className="nut nut--chinh" data-hanh-dong="them-moi-lich">
          + {viLichNhatKy.chung.themMoiLich}
        </Link>
      </div>

      <DaiThongBao tb={thongBao} />

      <BangDuLieu<LichPhatBT>
        nhan={t.tieuDe}
        cot={cot}
        dong={dsDong}
        khoaDong={(l) => l.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />

      {dangHuy ? (
        <HopXacNhan
          tieuDe={t.huyTieuDe}
          heQua={dienVao(t.huyHeQua, { ten: dangHuy.tenLichPhat, soDiaBan: dangHuy.diaBan?.length ?? 0 })}
          nhanThucHien={t.huy}
          onHuy={() => datDangHuy(null)}
          onThucHien={() => {
            const l = dangHuy;
            datDangHuy(null);
            void huyLich(l);
          }}
        />
      ) : null}
    </section>
  );
}

/* ------------------------------------ Soạn lịch ------------------------------------ */

const tl = viLichPhatThanh;

let demId = 0;
const idMoi = (): string => `k${Date.now().toString(36)}${(demId++).toString(36)}`;

function khungMoi(truoc: KhungGioSoan | undefined, ketThucTruoc: number | null): KhungGioSoan {
  // Khung mới nối tiếp khung cuối; ngày trống thì 05:00:00 như trang Lịch phát thanh.
  const batDau = truoc ? Math.min(ketThucTruoc ?? truoc.batDau, 86399) : 5 * 3600;
  return { id: idMoi(), batDau, ketThucTay: null, banTin: [] };
}

/** Bảng điện tử đã chọn ở ô "Chọn thiết bị". */
export type BangDaChon = Pick<TrangThaiThietBiBT, 'bangDienTuId' | 'tenThietBi' | 'nguonId'>;

/** Số ngày tối đa trải ra khi sửa một lịch hằng ngày. */
const TOI_DA_NGAY_TRAI = 366;

/**
 * Lịch đã lưu → bản soạn để SỬA. Chỉ giữ khung giờ CHƯA bắt đầu; mọi khung dùng chung danh sách
 * bản tin của lịch. Lịch hằng ngày (tạo trước đây) trải ra từng ngày trong khoảng ngày của nó.
 */
export function soanTuLichBT(
  l: Pick<ChiTietLichBT, 'loaiLich' | 'ngayBatDauPhat' | 'ngayKetThucPhat' | 'khungGio' | 'banTin'>,
  bayGioGiay: number,
  taoId: () => string,
): NgayPhatSoan[] {
  const banTin = idBanTinCuaLich(l);
  const theoNgay = new Map<string, KhungGioSoan[]>();
  const them = (bd: number, kt: number): void => {
    if (bd < bayGioGiay) return;
    const ngay = homNayVn(bd * 1000);
    const dauNgay = epochVn(ngay, 0);
    const ds = theoNgay.get(ngay) ?? [];
    ds.push({ id: taoId(), batDau: bd - dauNgay, ketThucTay: kt - dauNgay, banTin: [...banTin] });
    theoNgay.set(ngay, ds);
  };
  if (l.loaiLich === 'hang_ngay') {
    if (typeof l.ngayBatDauPhat === 'number' && typeof l.ngayKetThucPhat === 'number') {
      let dau = epochVn(homNayVn(l.ngayBatDauPhat * 1000), 0);
      for (let i = 0; i < TOI_DA_NGAY_TRAI && dau <= l.ngayKetThucPhat; i++, dau += MOT_NGAY_GIAY) {
        for (const k of l.khungGio ?? []) them(dau + k.batDau, dau + k.ketThuc);
      }
    }
  } else {
    for (const k of l.khungGio ?? []) them(k.batDau, k.ketThuc);
  }
  return [...theoNgay]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([ngay, khung]) => ({ ngay, khung: khung.sort((a, b) => a.batDau - b.batDau) }));
}

/**
 * Phạm vi lưu: chọn bảng cụ thể → gom theo xã của bảng (`dichId` = mã xã, `bangDienTu` = mã
 * bảng); không chọn → địa bàn đang chọn, mọi bảng trên địa bàn.
 */
export function phamViLuuBT(diaBan: string, bang: BangDaChon[]): LichPhatBTVao['phamVi'] {
  if (bang.length === 0) return [{ dichId: diaBan }];
  const theoXa = new Map<string, { dichId: string; bangDienTu: string[] }>();
  for (const d of bang) {
    const ma = d.nguonId || diaBan;
    const muc = theoXa.get(ma) ?? { dichId: ma, bangDienTu: [] };
    muc.bangDienTu.push(d.bangDienTuId);
    theoXa.set(ma, muc);
  }
  return [...theoXa.values()];
}

/**
 * Gom bản soạn thành các thân `POST schedules`: mỗi danh sách bản tin (đúng thứ tự) một lịch theo
 * ngày (khung giờ epoch tuyệt đối) — máy chủ một lịch một danh sách bản tin, như DV010. Gọi SAU
 * `kiemLoiLich`.
 */
export function gomThanhLichBT(
  tenLichPhat: string,
  dsNgay: NgayPhatSoan[],
  phanVungManHinh: PhanVungManHinhBT,
  phamVi: LichPhatBTVao['phamVi'],
  thoiLuongTheoMa: Record<string, number | null>,
): LichPhatBTVao[] {
  const nhom = new Map<string, KhungGioBT[]>();
  for (const n of [...dsNgay].sort((a, b) => a.ngay.localeCompare(b.ngay))) {
    for (const k of [...n.khung].sort((a, b) => a.batDau - b.batDau)) {
      const kt = ketThucKhung(k, thoiLuongTheoMa) ?? k.batDau;
      const khoa = k.banTin.join('|');
      const ds = nhom.get(khoa) ?? [];
      ds.push({ batDau: epochVn(n.ngay, k.batDau), ketThuc: epochVn(n.ngay, kt) });
      nhom.set(khoa, ds);
    }
  }
  const soNhom = nhom.size;
  return [...nhom].map(([khoa, khungGio], i) => ({
    tenLichPhat: soNhom > 1 ? `${tenLichPhat} (${i + 1}/${soNhom})` : tenLichPhat,
    loaiLich: 'theo_ngay' as const,
    phanVungManHinh,
    khungGio,
    banTin: khoa.split('|'),
    phamVi,
  }));
}

export function SoanLichBangTin({ api = API_LICH_BT_MAC_DINH }: { api?: ApiLichBT } = {}): ReactNode {
  const phien = dungPhien();
  const dieuHuong = useNavigate();
  const maTinh = phien.hoSo?.maTinh ?? '';
  /** Có `id` trên đường dẫn → sửa lịch đó thay vì tạo mới. */
  const { id: idSua } = useParams();

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  const dsBanTin = dungTaiDuLieu<BanTinChonBT[]>({
    tai: async (signal) =>
      (
        await api.banTinDanhSach(
          { trangThai: TRANG_THAI_CHON_PHAT, loaiBanTin: LOAI_BAN_TIN_BANG_TIN, page: 1, pageSize: 200 },
          signal,
        )
      ).items.map(banTinChonTuKho),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const [diaBan, datDiaBan] = useState(phien.phamViDangChon[0] ?? maTinh);
  const tenDiaBan = useMemo(() => {
    const nut = (cay.duLieu ?? []).find((n) => n.maDinhDanh === diaBan);
    return nut?.ten ?? (diaBan === maTinh ? (phien.hoSo?.tenTinh ?? diaBan) : diaBan);
  }, [cay.duLieu, diaBan, maTinh, phien.hoSo?.tenTinh]);

  const tenMacDinh = `${tenDiaBan} ${hienThiNgay(homNayVn()).replace(/-/g, '/')}`;
  const [tenLich, datTenLich] = useState<string | null>(null);
  const [phanVung, datPhanVung] = useState<PhanVungManHinhBT>('0');
  const [thietBi, datThietBi] = useState<BangDaChon[]>([]);
  const [dsNgay, datDsNgay] = useState<NgayPhatSoan[]>([]);
  /** Bản tin của lịch đang sửa (có thể không còn trong danh sách chọn). */
  const [banTinLich, datBanTinLich] = useState<BanTinTrongLichBT[]>([]);
  const [hopNgay, datHopNgay] = useState<{ saoChepTu?: string } | null>(null);
  const [hopBanTin, datHopBanTin] = useState<{ ngay: string; khungId: string } | null>(null);
  const [loi, datLoi] = useState<string[]>([]);
  const [dangLuu, datDangLuu] = useState(false);
  const [dangTaiLich, datDangTaiLich] = useState(Boolean(idSua));

  // Chế độ sửa: nạp lịch, dựng lại tên, phân vùng, địa bàn, bảng và các khung giờ chưa bắt đầu.
  useEffect(() => {
    if (!idSua) return undefined;
    const huy = new AbortController();
    datDangTaiLich(true);
    api
      .lichChiTiet(idSua, huy.signal)
      .then((l) => {
        datTenLich(l.tenLichPhat);
        datPhanVung(l.phanVungManHinh === '1' ? '1' : '0');
        const pv = l.phamVi ?? [];
        const coBang = pv.some((p) => (p.bangDienTu ?? []).length > 0);
        datDiaBan(coBang && pv.length > 1 ? maTinh : (pv[0]?.dichId ?? maTinh));
        datThietBi(
          coBang
            ? pv.flatMap((p) =>
                (p.bangDienTu ?? []).map((ma) => ({ bangDienTuId: ma, tenThietBi: ma, nguonId: p.dichId })),
              )
            : [],
        );
        datBanTinLich(l.banTin ?? []);
        datDsNgay(soanTuLichBT(l, Math.floor(Date.now() / 1000), idMoi));
      })
      .catch((e: unknown) => {
        if (!huy.signal.aborted) datLoi([dienVao(tl.loiTaiLich, { loi: loiThanhChuoi(e) })]);
      })
      .finally(() => {
        if (!huy.signal.aborted) datDangTaiLich(false);
      });
    return () => huy.abort();
    // Chỉ nạp lại khi đổi lịch đang sửa.
  }, [idSua]);

  const thoiLuongTheoMa = useMemo(() => {
    const r: Record<string, number | null> = {};
    for (const b of banTinLich) r[b.id] = b.thoiLuongGiay ?? null;
    for (const b of dsBanTin.duLieu ?? []) r[b.id] = b.thoiLuongGiay ?? null;
    return r;
  }, [banTinLich, dsBanTin.duLieu]);

  const doiNgay = (ngay: string, doi: (n: NgayPhatSoan) => NgayPhatSoan): void =>
    datDsNgay((cu) => cu.map((n) => (n.ngay === ngay ? doi(n) : n)));
  const doiKhung = (ngay: string, id: string, doi: (k: KhungGioSoan) => KhungGioSoan): void =>
    doiNgay(ngay, (n) => ({ ...n, khung: n.khung.map((k) => (k.id === id ? doi(k) : k)) }));

  const themNgay = (moi: string[]): void => {
    datDsNgay((cu) => {
      const co = new Set(cu.map((n) => n.ngay));
      return [...cu, ...moi.filter((x) => !co.has(x)).map((ngay) => ({ ngay, khung: [] }))].sort((a, b) =>
        a.ngay.localeCompare(b.ngay),
      );
    });
  };
  const saoChep = (tu: string, den: string[]): void => {
    datDsNgay((cu) => {
      const goc = cu.find((n) => n.ngay === tu);
      if (!goc) return cu;
      const banSao = (): KhungGioSoan[] =>
        goc.khung.map((k) => ({ ...k, id: idMoi(), banTin: [...k.banTin] }));
      const theoNgay = new Map(cu.map((n) => [n.ngay, n]));
      for (const ngay of den) theoNgay.set(ngay, { ngay, khung: banSao() });
      return [...theoNgay.values()].sort((a, b) => a.ngay.localeCompare(b.ngay));
    });
  };

  const luu = async (): Promise<void> => {
    const ten = (tenLich ?? tenMacDinh).trim();
    const dsLoi = [
      ...(ten ? [] : [tl.canTen]),
      ...(diaBan ? [] : [tl.canDiaBan]),
      ...kiemLoiLich(dsNgay, thoiLuongTheoMa, Math.floor(Date.now() / 1000)),
    ];
    datLoi(dsLoi);
    if (dsLoi.length > 0) return;
    const lich = gomThanhLichBT(ten, dsNgay, phanVung, phamViLuuBT(diaBan, thietBi), thoiLuongTheoMa);
    datDangLuu(true);
    const ketQua: { loai: 'ok' | 'loi'; noiDung: string }[] = [];
    try {
      for (const [i, than] of lich.entries()) {
        // Sửa: nhóm bản tin đầu ghi đè lịch đang sửa, nhóm khác (nếu tách) thành lịch mới.
        const kq = idSua && i === 0 ? await api.lichSua(idSua, than) : await api.lichTao(than);
        // Lưu luôn thành công; gửi xuống lỗi thì báo kèm để người dùng bấm "Phát lịch" lại.
        ketQua.push(thongBaoKetQuaGui(ketQuaGuiCuaLich(kq), dienVao(t.daLuuTen, { ten: than.tenLichPhat })));
      }
      const coLoi = ketQua.find((k) => k.loai === 'loi');
      const noiDung =
        coLoi?.noiDung ??
        (idSua
          ? dienVao(t.daCapNhat, { ten })
          : lich.length === 1
            ? (ketQua[0]?.noiDung ?? dienVao(t.daLuuTen, { ten }))
            : dienVao(t.daLuu, { so: lich.length }));
      dieuHuong(DUONG_DS, { state: { thongBao: noiDung, loai: coLoi ? 'loi' : 'ok' } });
    } catch (e) {
      datLoi([
        dienVao(tl.loiLuu, { loi: loiThanhChuoi(e) }),
        ...(ketQua.length > 0 ? [dienVao(t.daLuuMot, { so: ketQua.length, tong: lich.length })] : []),
      ]);
      datDangLuu(false);
    }
  };

  const khungDangChon = hopBanTin
    ? dsNgay.find((n) => n.ngay === hopBanTin.ngay)?.khung.find((k) => k.id === hopBanTin.khungId)
    : undefined;

  return (
    <section className="man-hinh lpt" data-man-hinh="soan-lich-bang-tin">
      <div className="lpt__dau">
        <h1>
          <button
            type="button"
            className="lpt__quay-lai"
            aria-label={tl.quayLai}
            onClick={() => dieuHuong(DUONG_DS)}
          >
            ←
          </button>
          {idSua ? tl.tieuDeSua : tl.tieuDe}
        </h1>
        <Link to={DUONG_DS} className="lpt__lien-ket">
          {tl.xemLichDaLap}
        </Link>
      </div>
      {dangTaiLich ? (
        <p className="lpt__nhan" role="status">
          {tl.dangTaiLich}
        </p>
      ) : null}

      <label className="lpt__nhan lpt__nhan--bb" htmlFor="slbt-ten">
        {tl.tenLich}
      </label>
      <input
        id="slbt-ten"
        className="lpt__o"
        value={tenLich ?? tenMacDinh}
        maxLength={50}
        onChange={(e) => datTenLich(e.target.value)}
      />

      <div className="lpt__hai-cot">
        <div>
          <span className="lpt__nhan lpt__nhan--bb">{tl.diaBan}</span>
          <ChonDiaBanCay
            className="lpt-cay"
            id="slbt-dia-ban"
            nhan={tl.diaBan}
            giaTri={diaBan}
            cay={cay.duLieu ?? []}
            nhanTatCa={tl.diaBanTatCa}
            onDoi={(v) => {
              datDiaBan(v || maTinh);
              datThietBi([]);
            }}
          />
        </div>
        <ChonBangDienTu
          diaBan={diaBan}
          daChon={thietBi}
          onDoi={datThietBi}
          trangThaiThietBi={api.trangThaiThietBi}
        />
      </div>

      <span className="lpt__nhan lpt__nhan--bb">{t.phanVungManHinh}</span>
      <ChonPhanVung giaTri={phanVung} onDoi={datPhanVung} />

      <div className="lpt__nhan">
        {dienVao(tl.dsNgay, { so: dsNgay.length })} <span aria-hidden="true">🗓</span>)
      </div>
      <div className="lpt__vung-ngay" data-vung="danh-sach-ngay">
        {dsNgay.map((n) => (
          <article key={n.ngay} className="lpt-ngay" data-ngay={n.ngay}>
            <div className="lpt-ngay__dau">
              <strong>{hienThiNgay(n.ngay)}</strong>
              <span className="lpt-ngay__nut">
                <button type="button" className="lpt-lk" onClick={() => datHopNgay({ saoChepTu: n.ngay })}>
                  ⧉ {tl.saoChep}
                </button>
                <button
                  type="button"
                  className="lpt-lk lpt-lk--do"
                  onClick={() => datDsNgay((cu) => cu.filter((x) => x.ngay !== n.ngay))}
                >
                  🗑 {tl.xoa}
                </button>
              </span>
            </div>
            {n.khung.length > 0 ? (
              <div className="lpt-khung lpt-khung--dau" aria-hidden="true">
                <span>{tl.cot.ten}</span>
                <span>{tl.cot.batDau}</span>
                <span>{tl.cot.ketThuc}</span>
                <span>{tl.cot.noiDung}</span>
                <span />
              </div>
            ) : null}
            {n.khung.map((k) => {
              const tong = tongThoiLuong(k.banTin, thoiLuongTheoMa);
              const kt = ketThucKhung(k, thoiLuongTheoMa);
              const nhapTay = k.banTin.length > 0 && tong === null;
              return (
                <div key={k.id} className="lpt-khung" data-khung={k.id}>
                  <input className="lpt__o" value={tenKhung(k.batDau, kt)} disabled aria-label={tl.cot.ten} />
                  <OGio
                    nhan={tl.cot.batDau}
                    giay={k.batDau}
                    onDoi={(g) => doiKhung(n.ngay, k.id, (x) => ({ ...x, batDau: g }))}
                  />
                  <OGio
                    nhan={tl.cot.ketThuc}
                    giay={kt ?? k.batDau}
                    tat={!nhapTay && k.ketThucTay === null}
                    goiY={nhapTay ? tl.ketThucTay : undefined}
                    onDoi={(g) => doiKhung(n.ngay, k.id, (x) => ({ ...x, ketThucTay: g }))}
                  />
                  <button
                    type="button"
                    className="lpt-khung__noi-dung"
                    data-hanh-dong="chon-ban-tin"
                    onClick={() => datHopBanTin({ ngay: n.ngay, khungId: k.id })}
                  >
                    {dienVao(tl.soBanTin, { so: k.banTin.length })}
                  </button>
                  <button
                    type="button"
                    className="lpt-khung__xoa"
                    aria-label={tl.xoaKhung}
                    onClick={() =>
                      doiNgay(n.ngay, (x) => ({ ...x, khung: x.khung.filter((y) => y.id !== k.id) }))
                    }
                  >
                    —
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="lpt-them-khung"
              data-hanh-dong="them-khung"
              onClick={() =>
                doiNgay(n.ngay, (x) => {
                  const cuoi = x.khung.at(-1);
                  return {
                    ...x,
                    khung: [...x.khung, khungMoi(cuoi, cuoi ? ketThucKhung(cuoi, thoiLuongTheoMa) : null)],
                  };
                })
              }
            >
              + {tl.themKhung}
            </button>
          </article>
        ))}
        <button
          type="button"
          className="nut nut--chinh lpt__them-ngay"
          data-hanh-dong="them-ngay"
          onClick={() => datHopNgay({})}
        >
          + {tl.themNgay}
        </button>
      </div>

      {loi.length > 0 ? (
        <ul className="lpt__loi" role="alert">
          {loi.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      ) : null}

      <div className="lpt__chan">
        <button type="button" className="nut" onClick={() => dieuHuong(DUONG_DS)}>
          {tl.huy}
        </button>
        <button
          type="button"
          className="nut nut--chinh"
          data-hanh-dong="luu-lich"
          disabled={dangLuu || dangTaiLich}
          onClick={() => void luu()}
        >
          {dangLuu ? tl.dangLuu : tl.luu}
        </button>
      </div>

      {hopNgay ? (
        <HopChonNgay
          tieuDe={
            hopNgay.saoChepTu
              ? dienVao(tl.lich.tieuDeSaoChep, { ngay: hienThiNgay(hopNgay.saoChepTu) })
              : undefined
          }
          coDinh={hopNgay.saoChepTu ? [hopNgay.saoChepTu] : dsNgay.map((n) => n.ngay)}
          onHuy={() => datHopNgay(null)}
          onXacNhan={(ds) => {
            if (hopNgay.saoChepTu) saoChep(hopNgay.saoChepTu, ds);
            else themNgay(ds);
            datHopNgay(null);
          }}
        />
      ) : null}

      {hopBanTin && khungDangChon ? (
        <HopChonBanTinBT
          tenKhung={`${hienThiNgay(hopBanTin.ngay)} ${tenKhung(khungDangChon.batDau, ketThucKhung(khungDangChon, thoiLuongTheoMa))}`}
          dsBanTin={dsBanTin.duLieu}
          dangTai={dsBanTin.trangThai === 'dang-tai'}
          daChon={khungDangChon.banTin}
          thoiLuongTheoMa={thoiLuongTheoMa}
          onHuy={() => datHopBanTin(null)}
          onXacNhan={(ds) => {
            doiKhung(hopBanTin.ngay, hopBanTin.khungId, (x) => ({ ...x, banTin: ds, ketThucTay: null }));
            datHopBanTin(null);
          }}
        />
      ) : null}
    </section>
  );
}

/* ------------------------------ Chọn phân vùng màn hình ------------------------------ */

/**
 * Ô chọn phân vùng màn hình cùng kiểu ô "Chọn địa bàn" (`dbc__*`). Không dùng `<select>` gốc:
 * danh sách thả xuống của trình duyệt bị lệch, cắt chữ trong khung trang.
 */
function ChonPhanVung({
  giaTri,
  onDoi,
}: {
  giaTri: PhanVungManHinhBT;
  onDoi: (v: PhanVungManHinhBT) => void;
}): ReactNode {
  const [mo, datMo] = useState(false);
  const goc = useRef<HTMLDivElement | null>(null);
  const idDs = useId();

  useEffect(() => {
    if (!mo) return;
    const dong = (e: MouseEvent): void => {
      if (goc.current && !goc.current.contains(e.target as Node)) datMo(false);
    };
    document.addEventListener('mousedown', dong);
    return () => document.removeEventListener('mousedown', dong);
  }, [mo]);

  const chon = (v: PhanVungManHinhBT): void => {
    onDoi(v);
    datMo(false);
  };

  return (
    <div className="dbc lpt-cay" ref={goc}>
      <button
        type="button"
        className="dbc__nut"
        aria-label={t.phanVungManHinh}
        aria-haspopup="listbox"
        aria-expanded={mo}
        aria-controls={idDs}
        onClick={() => datMo((x) => !x)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') datMo(false);
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!mo) datMo(true);
            else chon('1');
          }
          if (e.key === 'ArrowUp' && mo) {
            e.preventDefault();
            chon('0');
          }
        }}
      >
        <span className="dbc__chu">{viBangTin.phanVungManHinh[giaTri]}</span>
        <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
      </button>
      {mo ? (
        <div className="dbc__hop">
          <ul className="dbc__ds" role="listbox" id={idDs} aria-label={t.phanVungManHinh}>
            {(['0', '1'] as const).map((v) => (
              <li
                key={v}
                role="option"
                aria-selected={v === giaTri}
                className={`dbc__muc${v === giaTri ? ' dbc__muc--chon' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => chon(v)}
              >
                <span className="dbc__ten">{viBangTin.phanVungManHinh[v]}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------- Chọn bảng điện tử ------------------------------- */

const SO_BANG_HIEN = 50;

function ChonBangDienTu({
  diaBan,
  daChon,
  onDoi,
  trangThaiThietBi,
}: {
  diaBan: string;
  daChon: BangDaChon[];
  onDoi: (ds: BangDaChon[]) => void;
  trangThaiThietBi: ApiLichBT['trangThaiThietBi'];
}): ReactNode {
  const [mo, datMo] = useState(false);
  const [tim, datTim] = useState('');
  const timHoan = dungHoan(tim, 300);
  const goc = useRef<HTMLDivElement | null>(null);
  const idDs = useId();

  const tong = dungTaiDuLieu<number>({
    tai: async (signal) =>
      (await trangThaiThietBi({ nguonId: diaBan || undefined, page: 1, pageSize: 1 }, signal)).total,
    laRong: () => false,
    dangLoc: false,
    phuThuoc: [diaBan],
  });
  const ds = dungTaiDuLieu<TrangThaiThietBiBT[]>({
    tai: async (signal) =>
      (
        await trangThaiThietBi(
          { nguonId: diaBan || undefined, keyword: timHoan || undefined, page: 1, pageSize: SO_BANG_HIEN },
          signal,
        )
      ).items,
    laRong: (d) => d.length === 0,
    dangLoc: Boolean(timHoan),
    phuThuoc: [diaBan, timHoan, mo],
    tat: !mo,
  });

  useEffect(() => {
    if (!mo) return;
    const dong = (e: MouseEvent): void => {
      if (goc.current && !goc.current.contains(e.target as Node)) datMo(false);
    };
    document.addEventListener('mousedown', dong);
    return () => document.removeEventListener('mousedown', dong);
  }, [mo]);

  const tapChon = new Set(daChon.map((d) => d.bangDienTuId));
  const bat = (d: BangDaChon): void =>
    onDoi(
      tapChon.has(d.bangDienTuId)
        ? daChon.filter((x) => x.bangDienTuId !== d.bangDienTuId)
        : [...daChon, { bangDienTuId: d.bangDienTuId, tenThietBi: d.tenThietBi, nguonId: d.nguonId }],
    );

  return (
    <div className="lpt-tb" ref={goc}>
      <span className="lpt__nhan">{dienVao(tl.thietBi, { so: tong.duLieu ?? 0 })}</span>
      <div
        className="lpt-tb__o"
        role="combobox"
        aria-expanded={mo}
        aria-controls={idDs}
        tabIndex={0}
        onClick={() => datMo(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === 'ArrowDown') datMo(true);
          if (e.key === 'Escape') datMo(false);
        }}
      >
        {daChon.length === 0 ? (
          <span className="lpt-tb__the">{tl.tatCaThietBi}</span>
        ) : (
          daChon.map((d) => (
            <span key={d.bangDienTuId} className="lpt-tb__the">
              {d.tenThietBi || d.bangDienTuId}
              <button
                type="button"
                aria-label={`${tl.boChon} ${d.tenThietBi ?? d.bangDienTuId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  bat(d);
                }}
              >
                ×
              </button>
            </span>
          ))
        )}
        <span className="lpt-tb__mui" aria-hidden="true">
          ⌄
        </span>
      </div>
      {mo ? (
        <div className="lpt-tb__ds" id={idDs}>
          <input
            className="lpt__o"
            autoFocus
            placeholder={tl.timThietBi}
            value={tim}
            onChange={(e) => datTim(e.target.value)}
          />
          <ul role="listbox" aria-multiselectable="true">
            <li role="option" aria-selected={daChon.length === 0}>
              <label>
                <input type="checkbox" checked={daChon.length === 0} onChange={() => onDoi([])} />
                {tl.tatCaThietBi}
              </label>
            </li>
            {ds.trangThai === 'dang-tai' ? <li className="lpt-tb__phu">{tl.dangTaiThietBi}</li> : null}
            {ds.trangThai === 'rong' || ds.trangThai === 'rong-loc' ? (
              <li className="lpt-tb__phu">{tl.khongCoThietBi}</li>
            ) : null}
            {(ds.duLieu ?? []).map((d) => (
              <li key={d.bangDienTuId} role="option" aria-selected={tapChon.has(d.bangDienTuId)}>
                <label>
                  <input type="checkbox" checked={tapChon.has(d.bangDienTuId)} onChange={() => bat(d)} />
                  {d.tenThietBi || d.bangDienTuId} <small>{d.bangDienTuId}</small>
                </label>
              </li>
            ))}
            {(ds.duLieu ?? []).length >= SO_BANG_HIEN ? (
              <li className="lpt-tb__phu">{dienVao(tl.hienThiToiDa, { so: SO_BANG_HIEN })}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------- Chọn bản tin -------------------------------- */

function HopChonBanTinBT({
  tenKhung: ten,
  dsBanTin,
  dangTai,
  daChon,
  thoiLuongTheoMa,
  onHuy,
  onXacNhan,
}: {
  tenKhung: string;
  dsBanTin: BanTinChonBT[] | null;
  dangTai: boolean;
  daChon: string[];
  thoiLuongTheoMa: Record<string, number | null>;
  onHuy: () => void;
  onXacNhan: (ds: string[]) => void;
}): ReactNode {
  const c = tl.chonBanTin;
  const idTieuDe = useId();
  const [chon, datChon] = useState<string[]>(daChon);
  const [tim, datTim] = useState('');
  const q = boDau(tim.trim());
  const loc = (dsBanTin ?? []).filter(
    (b) => !q || boDau(b.tieuDe).includes(q) || b.banTinId.toLowerCase().includes(q),
  );
  const tong = tongThoiLuong(chon, thoiLuongTheoMa);
  const bat = (ma: string): void =>
    datChon((cu) => (cu.includes(ma) ? cu.filter((x) => x !== ma) : [...cu, ma]));

  return (
    <div className="man-che" role="presentation">
      <div className="hop-thoai lpt-bt" role="dialog" aria-modal="true" aria-labelledby={idTieuDe}>
        <div className="lpt-hop__dau">
          <h2 id={idTieuDe}>{dienVao(c.tieuDe, { khung: ten })}</h2>
          <button type="button" className="lpt-hop__dong" aria-label={tl.lich.dong} onClick={onHuy}>
            ×
          </button>
        </div>
        <input className="lpt__o" placeholder={c.tim} value={tim} onChange={(e) => datTim(e.target.value)} />
        <div className="lpt-bt__bang">
          <table className="bang">
            <thead>
              <tr>
                <th className="bang__giua">{c.cot.chon}</th>
                <th className="bang__giua">{c.cot.thuTu}</th>
                <th>{c.cot.tieuDe}</th>
                <th>{t.cotBanTin.loai}</th>
                <th>{t.cotBanTin.vungPhat}</th>
                <th className="bang__giua">{c.cot.thoiLuong}</th>
              </tr>
            </thead>
            <tbody>
              {dangTai ? (
                <tr>
                  <td colSpan={6}>{c.dangTai}</td>
                </tr>
              ) : loc.length === 0 ? (
                <tr>
                  <td colSpan={6}>{c.rong}</td>
                </tr>
              ) : (
                loc.map((b) => {
                  const viTri = chon.indexOf(b.id);
                  return (
                    <tr key={b.id} data-ban-tin={b.banTinId}>
                      <td className="bang__giua">
                        <input
                          type="checkbox"
                          aria-label={b.tieuDe}
                          checked={viTri >= 0}
                          onChange={() => bat(b.id)}
                        />
                      </td>
                      <td className="bang__giua">{viTri >= 0 ? viTri + 1 : ''}</td>
                      <td>{b.tieuDe}</td>
                      <td>{viBangTin.loaiBanTin[b.loaiBanTin] ?? b.loaiBanTin}</td>
                      <td>{viBangTin.vungPhat[b.vungPhat] ?? b.vungPhat}</td>
                      <td className="bang__giua">
                        {typeof b.thoiLuongGiay === 'number' ? hmsTuGiay(b.thoiLuongGiay) : c.chuaRo}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="lpt-bt__tong">
          {dienVao(c.tong, { so: chon.length, tl: tong === null ? c.chuaRo : hmsTuGiay(tong) })}
        </p>
        <div className="lpt-hop__nut">
          <button type="button" className="nut lpt-nut-huy" onClick={onHuy}>
            {c.huy}
          </button>
          <button
            type="button"
            className="nut nut--chinh"
            data-hanh-dong="xac-nhan-ban-tin"
            onClick={() => onXacNhan(chon)}
          >
            {c.xacNhan}
          </button>
        </div>
      </div>
    </div>
  );
}
